/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, ReactEventHandler} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import CloseButton from '../components/buttons/CloseButton';
import {PrimaryButton} from '../components/buttons/PrimaryButton';
import {SecondaryButton} from '../components/buttons/SecondaryButton';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import Indicator, {IndicatorProps} from '../components/indicators/Indicator';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import {ContentLoader} from '../components/loading/ContentLoader';
import Render from '../render/Render';
import {toggleThemeStyle} from '../theme/helpers';
import {
  dlsColor,
  dlsShadow,
  dlsTypography,
  dlsZIndex
} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {Color, DLSTheme} from '../types/types';

// defines the prop definitions for the session timeout ...
export interface UserNotificationDialogProps extends IndicatorProps {
  actionsClassName?: string;
  className?: string;
  icon?: ReactElement;
  infoIcon?: ReactElement;
  primaryIcon?: ReactElement;
  capitalize?: boolean;
  show: boolean;
  title: string;
  infoText?: string;
  message: string | JSX.Element;
  actionLabel?: string | JSX.Element;
  actionDisabled?: boolean;
  dismissLabel?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  zIndex?: number;
  maxWidth?: number;
  iconMargin?: string;
  allowWhitespace?: boolean;
  showInfo?: boolean;
  curvedCorners?: boolean;
  onAction?: () => void;
  onDismiss?: () => void;
  onSecondaryAction?: () => void;
  onPrimaryAction?: () => void;
  primaryDisabled?: boolean;
  isLoading?: boolean;
  hasContextAction?: boolean;
  contextActionLabel?: string | JSX.Element;
  onContextActionClick?: () => void;
  onBackdropClick?: ReactEventHandler;
  showDismissAction?: boolean;
  loading?: boolean;
  showCloseIcon?: boolean;
  inProgressLabel?: string;
  actionClassName?: string;
};

// defines the styles used by this component ...
const useStyles = makeStyles<DLSTheme, UserNotificationDialogProps>(({dls}) => ({
  icon : {
    display       : 'inline-block',
    verticalAlign : 'top',
    margin        : ({iconMargin}) => iconMargin || undefined,
    paddingTop    : ({indicator}) => indicator ? -3 : 2,
    marginLeft    : '0',
    marginRight   : 8
  },
  paper : toggleThemeStyle({
    borderRadius : ({curvedCorners}) => curvedCorners ? '5px' : 0,
    maxWidth     : ({maxWidth}) => maxWidth ? `${maxWidth}px` : undefined,
    margin       : '25px !important'
  }, {
    opacity         : 1,
    borderTop       : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow       : dlsShadow(2),
    borderRadius    : '12px',
    backgroundColor : `${dlsColor('white')} !important`,
    maxWidth        : ({maxWidth}) => maxWidth ? `${maxWidth}px !important` : undefined
  }),
  userNotificationRoot : {
    zIndex : ({zIndex}) => `${zIndex ?? dlsZIndex('userNotificationDialogRoot')} !important` as any
  },
  title : toggleThemeStyle({
    ...dlsTypography('h4'),
    textTransform : ({capitalize}) => capitalize ? 'uppercase' : 'none',
    padding       : '20px 20px 0px 24px',
    lineHeight    : '24px',
    letterSpacing : 1
  }, {
    color         : dlsColor('grey1'),
    display       : 'flex',
    fontSize      : '16px',
    position      : 'relative',
    alignItems    : 'center',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  titleText : {
    flex         : 1,
    minWidth     : 0,
    paddingRight : '48px'
  },
  content : toggleThemeStyle({
    whiteSpace : ({allowWhitespace}) => allowWhitespace ? 'pre-line' : undefined,
    padding    : '0 20px !important',
    margin     : '10px 0 15px'
  }, {
    color         : dlsColor('grey1'),
    padding       : '0px 24px',
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  contentText : {
    fontSize      : 14,
    fontFamily    : 'Inter',
    marginBottom  : '0 !important',
    letterSpacing : '0 !important'
  },
  actions : {
    padding        : ({onAction}) => onAction ? '0px 18px 8px !important' : '0px 5px !important',
    marginTop      : '-6',
    justifyContent : 'flex-start'
  },
  currentActions : {
    marginTop      : '0',
    justifyContent : 'flex-end'
  },
  center : {
    height         : '100%',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    padding        : 20
  },
  currentCloseButton : {
    top      : '7px',
    right    : '19px',
    position : 'absolute'
  },
  infoAction : {
    gap             : '4px',
    height          : '32px',
    display         : 'flex',
    padding         : '8px',
    borderRadius    : '8px',
    backgroundColor : dlsColor('grey6')
  },
  infoText : {
    color         : dlsColor('grey1'),
    fontFamily    : 'Roboto',
    fontSize      : '12px',
    fontWeight    : 400,
    lineHeight    : '14px',
    letterSpacing : '0'
  }
}));

/**
 * The UserNotification dialog serves to provide the generic dialog notification
 * which is to be used for prompting a user before making an important change,
 * such as deleting an item, changing their password, etc..
 *
 * @see https://www.figma.com/file/z9yUzIaskZRhgpYIhLyh91/03_Hero-Screens?node-id=632%3A82003
 *
 *
 * ```typescript
 *   import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
 *
 *   export const SessionTimeout = ({show, onLogin}: SessionTimeoutProps) => {
 *     return (
 *       <UserNotificationDialog show={show}
 *         icon={<NotificationDisconnectIcon />}
 *         title={translate('notifications:session.title')}
 *         message={translate('notifications:session.message')}
 *         actionLabel={translate('notifications:session.login')}
 *         onAction={onLogin} />
 *     );
 *   };
 *
 * ```
 *
 */
export const UserNotificationDialog = (props: UserNotificationDialogProps) => {
  const {
    actionsClassName,
    icon,
    className,
    show,
    title,
    message,
    infoIcon,
    infoText,
    primaryIcon,
    actionLabel,
    inProgressLabel = '',
    primaryLabel,
    secondaryLabel,
    dismissLabel,
    indicator,
    onAction,
    onDismiss,
    onPrimaryAction,
    onSecondaryAction,
    showInfo = false,
    actionClassName,
    actionDisabled = false,
    isLoading = false,
    onBackdropClick,
    showDismissAction = true,
    showCloseIcon = true,
    loading = false,
    contextActionLabel = '',
    onContextActionClick,
    hasContextAction = false,
    primaryDisabled = false
  } = props;

  const classes                     = useStyles(props);
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const dismissLabelStyle           = {color: dlsColor('grey3')};
  const dismissHoverColor           = dlsColor('grey6');

  if (show) {
    const hasActions = Boolean(onAction || onDismiss);

    return (
      <Dialog open classes={{
        root  : classnames(classes.userNotificationRoot, className),
        paper : classes.paper
      }}
      onBackdropClick={onBackdropClick}>
        <DialogTitle className={classes.title}>
          <Render.If condition={icon}>
            <div className={classes.icon}>{icon}</div>
          </Render.If>
          <Render.If condition={indicator}>
            <div className={classes.icon}><Indicator variant={indicator}/></div>
          </Render.If>
          <span className={isCurrentTheme ? classes.titleText : ''}>
            {title}
          </span>
          <Render.If condition={isCurrentTheme && showCloseIcon}>
            <div className={classes.currentCloseButton}>
              <CloseButton onClick={onDismiss} />
            </div>
          </Render.If>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Render>
            <Render.If condition={isLoading}>
              <div className={classes.center}>
                <ContentLoader removeMargin height={'20px'} width={'20px'} />
              </div>
            </Render.If>
            <Render.Else>
              <DialogContentText className={isCurrentTheme ? classes.contentText : ''}>
                {message}
              </DialogContentText>
            </Render.Else>
          </Render>
          <Render.If condition={showInfo}>
            <div className={classes.infoAction}>
              <span className={classes.infoIcon}>
                {infoIcon}
              </span>
              <span className={classes.infoText}>
                {infoText}
              </span>
            </div>
          </Render.If>
        </DialogContent>
        <Render.If condition={hasContextAction}>
          <DialogActions className={isCurrentTheme ? classes.currentActions : classes.actions}>
            <TertiaryButton style={{paddingLeft: 8}} onClick={onContextActionClick}>
              {contextActionLabel}
            </TertiaryButton>
          </DialogActions>
        </Render.If>
        <Render.If condition={hasActions}>
          <DialogActions className={classnames(isCurrentTheme
            ? classes.currentActions
            : classes.actions, actionsClassName
          )}>
            {onAction &&
              <TertiaryButton style={!isCurrentTheme ? {padding: 0} : undefined}
                className={actionClassName}
                disabled={actionDisabled}
                onClick={onAction}
                usecaactive={loading}
                noUnderlineOnFocus>
                <Render.If condition={loading}>
                  <ButtonSpinner size={24} />
                  <Render.If condition={inProgressLabel !== ''}>
                    &nbsp;{inProgressLabel}
                  </Render.If>
                </Render.If>
                <Render.If condition={!loading}>
                  {actionLabel}
                </Render.If>
              </TertiaryButton>
            }
            {onDismiss && showDismissAction &&
              <TertiaryButton onClick={onDismiss} isAlt altColor={Color.grey3}
                labelStyle={isCurrentTheme && dismissLabelStyle}
                hoverColor={isCurrentTheme && dismissHoverColor}>
                {dismissLabel}
              </TertiaryButton>
            }
            {onPrimaryAction &&
              <PrimaryButton startIcon={primaryIcon} disabled={primaryDisabled} onClick={onPrimaryAction}>
                {primaryLabel}
              </PrimaryButton>
            }
            {onSecondaryAction &&
              <SecondaryButton onClick={onSecondaryAction}>
                {secondaryLabel}
              </SecondaryButton>
            }
          </DialogActions>
        </Render.If>
      </Dialog>
    );
  }
  return null;
};
export default UserNotificationDialog;
